import { useEffect, useState } from "react";
import { RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * DashboardMeta
 *
 * Compact "Last updated … · Refresh" strip that sits under a page header.
 * Accepts an ISO timestamp (`fetchedAt`) from the server (when available) or
 * falls back to the client-side "now" when the data was loaded. Shows a
 * spinning icon while `loading` is true, and a muted error inline when
 * `error` is non-null.
 *
 * Props:
 *   fetchedAt:   ISO string from server, or null
 *   loading:     boolean — when true, refresh button spins + is disabled
 *   error:       string | null — rendered as a muted inline warning
 *   onRefresh:   () => void — called when the refresh button is clicked
 *   connected:   optional boolean — when false, shows a "not connected" pill
 *   connectedLabel: optional override for the connection pill text
 */
export function DashboardMeta({
  fetchedAt,
  loading,
  error,
  onRefresh,
  connected,
  connectedLabel,
}) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  const when = fetchedAt ? new Date(fetchedAt).getTime() : null;
  const humanAge = when ? relative(now - when) : "—";

  return (
    <div
      className="flex items-center gap-3 text-xs text-brand-primary/60"
      data-testid="dashboard-meta"
    >
      <span>
        Last updated{" "}
        <span className="font-medium text-brand-primary/80" data-testid="dashboard-last-updated">
          {humanAge}
        </span>
      </span>
      {connected === false && (
        <span
          className="inline-flex items-center gap-1 text-terracotta font-medium"
          data-testid="dashboard-connection-pill"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-terracotta inline-block" />
          {connectedLabel || "Not connected"}
        </span>
      )}
      {error && (
        <span
          className="inline-flex items-center gap-1 text-terracotta"
          data-testid="dashboard-error"
          title={error}
        >
          <AlertCircle className="h-3 w-3" />
          <span className="truncate max-w-[320px]">{error}</span>
        </span>
      )}
      {onRefresh && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onRefresh}
          disabled={loading}
          data-testid="dashboard-refresh"
          className="h-7 px-2 rounded-full text-brand-primary/70 hover:text-terracotta hover:bg-cream"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          <span className="ml-1">{loading ? "Refreshing…" : "Refresh"}</span>
        </Button>
      )}
    </div>
  );
}

function relative(ms) {
  if (ms == null || Number.isNaN(ms)) return "—";
  const sec = Math.max(0, Math.floor(ms / 1000));
  if (sec < 30) return "just now";
  if (sec < 90) return "a minute ago";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? "" : "s"} ago`;
}
